import { Deserializable } from '../../protocols/deserializable';
import { LookAheadItem } from '../../../views/shared/components/search-with-look-ahead/look-ahead-list/look-ahead-item/protocol/look-ahead-item';
import { BaseUser } from '../../base/base-user';
import { UserSession } from './user-session';
import { Address } from './address';
import { UserChallengeName } from '../enum/user-challenge.name';
import * as moment from 'moment/moment';
import { PronounTypeEnum } from '../enum/pronoun-type.enum';
import { MemberRecordComment } from '../../../views/records/components/members/ip-view-member-record/components/ip-member-record-comments/ip-member-record-comment';
import { Account } from '../../transactions/account';
import { GenderTypeEnum } from '../enum/gender-type';
import { Gender } from './gender';
import { MfaType, MfaTypeIdEnum } from '../../types/dto/mfa-type';
import { EmploymentHistory } from './employment-history';
import { MostRecentInvite } from './most-recent-invite';
import { MemberRelationshipStatus } from './member-relationship-status';
import { BankAccount } from '../../../views/banking/models/bank-account';
import { Fiduciary } from '../../../views/fiduciaries/models/fiduciary';
import { MemberStatusType } from './member-status-type';
import { MemberStatusTypeEnum } from '../enum/member-status-type-enum';
import { StatusPill } from '../../shared/status-pill';
import { MemberStatusUtils } from '../../../utils/member-status-utils';

export class MemberUser extends BaseUser implements Deserializable, LookAheadItem {
  public birthDate!: string;
  public accounts: Account[] = [];
  public genderId: GenderTypeEnum;
  public gender: Gender;
  public middleName: string;
  public primaryPhoneNumber: string;
  public primaryPhoneNumberCountryCode: string;
  public secondaryPhoneNumber: string;
  public secondaryPhoneNumberCountryCode: string;
  public socialInsuranceNumber: string;
  public preferredName: string;
  public deathDate: string;
  public contactInformationConfirmed: boolean;
  public employmentHistory!: EmploymentHistory[];
  public pronoun: Pronoun | null;
  public pronounId: PronounTypeEnum | null;
  public comments!: MemberRecordComment[];
  public mfaTypeId!: MfaTypeIdEnum;
  public mfaType!: MfaType;
  public mostRecentInvite!: MostRecentInvite;
  public relationshipStatus!: MemberRelationshipStatus;
  public bankAccounts!: BankAccount[];
  public legalFiduciary!: Fiduciary | null;
  public statusId!: MemberStatusTypeEnum;
  public status!: MemberStatusType;
  public allowThirdPartyContributions!: boolean;

  // Not From API
  public formattedBirthDate!: string;
  public mfaActive!: boolean;
  public statusPill!: StatusPill;

  constructor(
    id: number,
    firstName: string,
    lastName: string,
    email: string,
    birthDate: Date,
    genderId: number,
    gender: Gender,
    addressId: string,
    address: Address,
    passwordChangedDate: string | null,
    eulaConfirmation: boolean,
    eulaConfirmationDate: string | null,
    lastLoginDate: string | null,
    userSession: UserSession | null,
    challengeName: UserChallengeName | null,
    sessionId: string | null,
    middleName: string,
    primaryPhoneNumber: string,
    primaryPhoneNumberCountryCode: string,
    secondaryPhoneNumber: string,
    secondaryPhoneNumberCountryCode: string,
    socialInsuranceNumber: string,
    preferredName: string,
    deathDate: string,
    contactInformationConfirmed: boolean,
    pronoun: Pronoun | null,
    pronounId: PronounTypeEnum | null
  ) {
    super(
      id,
      firstName,
      lastName,
      email,
      passwordChangedDate,
      eulaConfirmation,
      eulaConfirmationDate,
      lastLoginDate,
      userSession,
      challengeName,
      sessionId,
      addressId,
      address
    );
    this.genderId = genderId;
    this.gender = gender;
    this.middleName = middleName;
    this.primaryPhoneNumber = primaryPhoneNumber;
    this.primaryPhoneNumberCountryCode = primaryPhoneNumberCountryCode;
    this.secondaryPhoneNumber = secondaryPhoneNumber;
    this.secondaryPhoneNumberCountryCode = secondaryPhoneNumberCountryCode;
    this.socialInsuranceNumber = socialInsuranceNumber;
    this.preferredName = preferredName;
    this.deathDate = deathDate;
    this.contactInformationConfirmed = contactInformationConfirmed;
    this.pronoun = pronoun;
    this.pronounId = pronounId;
  }

  public onDeserialize() {
    super.onDeserialize();
    if (!!this.status) {
      this.status = window?.injector?.Deserialize?.instanceOf(MemberStatusType, this.status);
    }
    this.statusPill = MemberStatusUtils.statusPill(this.statusId);
    if (!!this.address) {
      this.address = window?.injector?.Deserialize?.instanceOf(Address, this.address);
    }
    if (!!this.employmentHistory) {
      this.employmentHistory = window?.injector?.Deserialize?.arrayOf(EmploymentHistory, this.employmentHistory);
    }
    if (!!this.birthDate) {
      const date = moment(this.birthDate.split('T')[0]);
      this.formattedBirthDate = date.format('DD / MM / YYYY');
    }
    if (!!this.accounts) {
      this.accounts = window?.injector?.Deserialize?.arrayOf(Account, this.accounts);
    }
    if (this.mfaTypeId !== MfaTypeIdEnum.None) {
      this.mfaActive = true;
    }
    if (!!this.mostRecentInvite) {
      this.mostRecentInvite = window?.injector?.Deserialize?.instanceOf(MostRecentInvite, this.mostRecentInvite);
    }
    if (this.relationshipStatus) {
      this.relationshipStatus = window?.injector?.Deserialize?.instanceOf(
        MemberRelationshipStatus,
        this.relationshipStatus
      );
    }
    if (this.bankAccounts) {
      this.bankAccounts = window?.injector?.Deserialize?.arrayOf(BankAccount, this.bankAccounts);
    }
    if (this.legalFiduciary) {
      this.legalFiduciary = window.injector.Deserialize.instanceOf(Fiduciary, this.legalFiduciary);
    }
  }

  // Look ahead item interface

  lookAheadDisabled(): boolean {
    return false;
  }

  lookAheadDisabledMessage(): string {
    return 'disabled';
  }
}

export class Pronoun implements Deserializable {
  public id!: PronounTypeEnum;
  public name!: string;

  onDeserialize(): void {}
}
